import * as React from 'react';
import { cn } from '../../lib/utils';
import { VariantProps, cva } from 'class-variance-authority';

/**
 * Text input. The default look is glp-frontend's `components/helpers/input`;
 * `inputSize="lg"` is glp's form-field input (`components/atoms/Input`: 48px,
 * darker border), which glp's create/edit forms use. `aria-invalid` (what
 * react-hook-form callers set from `errors.x`) turns the border red.
 */
const inputVariants = cva(
  'flex h-10 w-full rounded-[19px] border border-gray-200 bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus:outline-none focus:border-global-green/40 focus:ring-1 focus:ring-global-green/20 disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid=true]:border-red-500 aria-[invalid=true]:focus:ring-red-500/20 !m-0',
  {
    variants: {
      variant: {
        rounded: 'rounded-[19px] border-[2px] !border-slate-200',
      },
      inputSize: {
        default: '',
        lg: 'h-12 border-gray-400 placeholder:text-black/50',
      },
    },
    defaultVariants: { inputSize: 'default' },
  },
);

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof inputVariants> {}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, variant, inputSize, ...props }, ref) => {
    return (
      <input
        type={type}
        className={cn(inputVariants({ variant, inputSize, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Input.displayName = 'Input';

export { Input, inputVariants };
